import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { usePageMeta, PAGE_META } from '../utils/usePageMeta';
import { submitBid, submitApplication, getTopBid, getBidCount, getCurrentRoom, getLedger } from './innovatorsData';
import './InnovatorsPage.css';

// Founders apply through Luma (approval-gated). Permanent calendar URL.
const LUMA_CALENDAR = 'https://lu.ma/grupa-innovators';

// Countdown to the next Wednesday room, in Pacific Time (handles DST + any viewer TZ).
const ROOM_HOUR = 17; // 5:00 PM PT. Set this to the real room start time.
function nextRoom() {
  const tz = 'America/Los_Angeles';
  const now = new Date();
  const ptNow = new Date(now.toLocaleString('en-US', { timeZone: tz }));
  const offset = now.getTime() - ptNow.getTime();
  const t = new Date(ptNow);
  const day = t.getDay(); // 3 = Wednesday
  let add = (3 - day + 7) % 7;
  t.setHours(ROOM_HOUR, 0, 0, 0);
  if (add === 0 && ptNow > t) add = 7;
  t.setDate(t.getDate() + add);
  return new Date(t.getTime() + offset);
}
// Bidding window for a given Wednesday room: opens Monday, closes Tuesday (both PT).
const BID_OPEN_HOUR = 12;  // Monday 12:00 PM PT. Change to move the open time.
const BID_CLOSE_HOUR = 12; // Tuesday 12:00 PM PT
function windowFor(roomTarget) {
  const tz = 'America/Los_Angeles';
  const ptRoom = new Date(roomTarget.toLocaleString('en-US', { timeZone: tz }));
  const offset = roomTarget.getTime() - ptRoom.getTime();
  const open = new Date(ptRoom); open.setDate(open.getDate() - 2); open.setHours(BID_OPEN_HOUR, 0, 0, 0);
  const close = new Date(ptRoom); close.setDate(close.getDate() - 1); close.setHours(BID_CLOSE_HOUR, 0, 0, 0);
  return { open: new Date(open.getTime() + offset), close: new Date(close.getTime() + offset) };
}
function remaining(target) {
  const diff = Math.max(0, target - new Date());
  return { d: Math.floor(diff / 864e5), h: Math.floor(diff / 36e5) % 24, m: Math.floor(diff / 6e4) % 60, s: Math.floor(diff / 1e3) % 60 };
}
const pad = (n) => String(n).padStart(2, '0');
const money = (n) => '$' + Number(n).toLocaleString('en-US');

const ROOM = [
  { n: 'The room', h: 'Fifteen. No more.', p: 'Fifteen of the best founders building right now, hand-picked from the current YC batch and the a16z portfolio, vetted one by one. If you are not building something that matters, you are not in the room.' },
  { n: 'The work', h: 'Three hours. Real work.', p: 'A working war-room, not a mixer. Live growth teardowns, GTM, and the problems nobody solves at a networking event. No stage. No pitches. No decks.' },
  { n: 'Why it exists', h: 'Builders get sharper around builders.', p: 'The people building the future get better, faster, in the same room. This is where they push each other, in private, every single week. That is the whole point.' },
];

function Modal({ title, onClose, children }) {
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onClose]);
  return (
    <div className="innov-modal-backdrop">
      <button type="button" className="innov-modal-scrim" aria-label="Close dialog" onClick={onClose} />
      <div className="innov-modal" role="dialog" aria-modal="true" aria-label={title}>
        <button type="button" className="innov-modal-x" aria-label="Close" onClick={onClose}>×</button>
        <h3 className="innov-modal-title">{title}</h3>
        {children}
      </div>
    </div>
  );
}

function BidForm({ eventDate, onDone }) {
  const [f, setF] = useState({ fund: '', anchor_name: '', email: '', amount: '', display_ok: true });
  const [state, setState] = useState('idle'); // idle | sending | done | error
  const [err, setErr] = useState('');
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setState('sending'); setErr('');
    try {
      await submitBid({ event_date: eventDate, fund: f.fund.trim(), anchor_name: f.anchor_name.trim(), email: f.email.trim(), amount: Number(f.amount), display_ok: f.display_ok });
      setState('done'); onDone && onDone();
    } catch (e2) { setState('error'); setErr(e2.message || 'Something went wrong'); }
  };
  if (state === 'done') return <p className="innov-form-done">Bid received. You are on the board. If you hold the top bid at close, we email you an invoice to lock the seat.</p>;
  return (
    <form className="innov-form" onSubmit={submit}>
      <label>Fund<input required value={f.fund} onChange={set('fund')} placeholder="e.g. Founders Fund" /></label>
      <label>Lead investor (shown on the leaderboard)<input required value={f.anchor_name} onChange={set('anchor_name')} placeholder="Your name" /></label>
      <label>Email (private, never shown)<input required type="email" value={f.email} onChange={set('email')} placeholder="you@fund.com" /></label>
      <label>Your bid (USD)<input required type="number" min="1" step="1" value={f.amount} onChange={set('amount')} placeholder="25000" /></label>
      <label className="innov-check"><input type="checkbox" checked={f.display_ok} onChange={set('display_ok')} /> Show our firm and lead investor publicly on the leaderboard.</label>
      {state === 'error' && <p className="innov-form-err">{err}</p>}
      <button type="submit" className="innov-btn primary block" disabled={state === 'sending'}>{state === 'sending' ? 'Placing…' : 'Place bid →'}</button>
      <p className="innov-fine">Bids are public. Highest bid at Tuesday 12:00 PM PT wins. If the winner does not wire within 2 hours, it passes to the next bid.</p>
    </form>
  );
}

function ApplyForm() {
  const [f, setF] = useState({ name: '', email: '', company: '', portfolio: 'YC', building: '' });
  const [state, setState] = useState('idle');
  const [err, setErr] = useState('');
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setState('sending'); setErr('');
    try {
      await submitApplication({ name: f.name.trim(), email: f.email.trim(), company: f.company.trim(), portfolio: f.portfolio, building: f.building.trim() });
      setState('done');
    } catch (e2) { setState('error'); setErr(e2.message || 'Something went wrong'); }
  };
  if (state === 'done') return <p className="innov-form-done">Application received. We read every one ourselves. If you are in, you get the address.</p>;
  return (
    <form className="innov-form" onSubmit={submit}>
      <label>Name<input required value={f.name} onChange={set('name')} /></label>
      <label>Email<input required type="email" value={f.email} onChange={set('email')} /></label>
      <label>Company<input required value={f.company} onChange={set('company')} /></label>
      <label>Backed by<select value={f.portfolio} onChange={set('portfolio')}><option>YC</option><option>a16z</option><option>Other</option></select></label>
      <label>What are you building?<textarea rows="3" value={f.building} onChange={set('building')} /></label>
      {state === 'error' && <p className="innov-form-err">{err}</p>}
      <button type="submit" className="innov-btn primary block" disabled={state === 'sending'}>{state === 'sending' ? 'Sending…' : 'Apply →'}</button>
    </form>
  );
}

export function InnovatorsPage() {
  usePageMeta(PAGE_META.innovators);
  const [target] = useState(nextRoom);
  const bidWin = useMemo(() => windowFor(target), [target]);
  const [, setTick] = useState(0);
  const [leader, setLeader] = useState(null);
  const [bidCount, setBidCount] = useState(0);
  const [ledger, setLedger] = useState([]);
  const [room, setRoom] = useState(null);
  const [modal, setModal] = useState(null); // 'bid' | 'apply' | null
  const eventDate = target.toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });

  useEffect(() => {
    const id = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const t = remaining(target);
  const openRem = remaining(bidWin.open);
  const closeRem = remaining(bidWin.close);
  const phase = Date.now() < bidWin.open.getTime() ? 'opens' : Date.now() < bidWin.close.getTime() ? 'open' : 'closed';
  const closingSoon = phase === 'open' && closeRem.d === 0; // final hours of bidding

  const refresh = useCallback(() => {
    getTopBid().then(setLeader);
    getBidCount().then(setBidCount);
  }, []);
  useEffect(() => {
    refresh();
    getLedger().then(setLedger);
    getCurrentRoom().then(setRoom);
    const id = setInterval(refresh, 15000);
    return () => clearInterval(id);
  }, [refresh]);

  const closeModal = useCallback(() => setModal(null), []);

  return (
    <div className="innov-page brand-innov">
      <Navigation theme="light" />
      <main id="main-content" tabIndex="-1" className="innov">

        <header className="innov-hero">
          <div className="innov-eyebrow">GrupaAI Innovators · South Park, San Francisco · Wednesdays</div>
          <h1 className="innov-h1">The room the best founders <em>fight</em> to get into.</h1>
          <p className="innov-lede">
            Every Wednesday in South Park, fifteen of the best founders in the world do real
            work. No stage, no pitches, no audience. One fund sponsors the room, and bids for
            the seat.
          </p>
          <div className="innov-cta-row">
            <a className="innov-btn primary" href={LUMA_CALENDAR} target="_blank" rel="noopener noreferrer">Apply for a seat</a>
            <a className="innov-btn ghost" href="#auction">For funds →</a>
          </div>

          <a className={`innov-live${phase === 'open' ? ' active' : ''}`} href="#auction">
            <span className="innov-live-dot" />
            {phase === 'opens' && <span>Bidding opens Monday 12:00 PM PT · in {openRem.d > 0 ? `${openRem.d}d ` : ''}{pad(openRem.h)}h {pad(openRem.m)}m</span>}
            {phase === 'open' && (leader
              ? <span>A fund is leading the room at <b>{money(leader.amount)}</b>{bidCount > 1 ? ` · ${bidCount} funds bidding` : ''}</span>
              : <span>Bidding is open · be the first fund to back the room</span>)}
            {phase === 'closed' && <span>This week&apos;s seat is claimed · next room Wednesday</span>}
            <span className="innov-live-cta">See the board →</span>
          </a>

          <div className="innov-ticker">
            <div className="innov-ticker-l">Next room · <b>Wednesday, 5:00 PM PT, South Park</b></div>
            <div className="innov-clock" aria-label="time until the next room">
              {[['DAYS', t.d], ['HRS', t.h], ['MIN', t.m], ['SEC', t.s]].map(([lbl, val]) => (
                <span key={lbl}><b>{pad(val)}</b><small>{lbl}</small></span>
              ))}
            </div>
          </div>

          {room && room.roster && (
            <div className="innov-roster">In the room this Wednesday · <b>{room.roster}</b></div>
          )}
        </header>

        <section className="innov-sec">
          <div className="innov-label">What the room is</div>
          <div className="innov-three">
            {ROOM.map((c) => (
              <div className="innov-cell" key={c.n}>
                <div className="innov-cell-n">{c.n}</div>
                <h3>{c.h}</h3>
                <p>{c.p}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="innov-sec innov-apply" id="apply">
          <div className="innov-label center">For founders</div>
          <h2 className="innov-h2 center">Fifteen seats. Every Wednesday. Apply for yours.</h2>
          <p className="innov-apply-p">YC and a16z portfolio founders. We read every application ourselves. If you are in, you get the address.</p>
          <a className="innov-btn primary" href={LUMA_CALENDAR} target="_blank" rel="noopener noreferrer">Apply for a seat →</a>
        </section>

        <section className="innov-sec" id="auction">
          <div className="innov-label">For funds</div>

          <div className={`innov-leader${closingSoon ? ' urgent' : ''}`}>
            <div className="innov-leader-tag">
              <span className="innov-live-dot" />
              {phase === 'opens' && <> Bidding opens Monday 12:00 PM PT · in {openRem.d > 0 ? `${openRem.d}d ` : ''}{pad(openRem.h)}:{pad(openRem.m)}:{pad(openRem.s)}</>}
              {phase === 'open' && <> Currently leading · bidding closes in {closeRem.d > 0 ? `${closeRem.d}d ` : ''}{pad(closeRem.h)}:{pad(closeRem.m)}:{pad(closeRem.s)}</>}
              {phase === 'closed' && <> Bidding closed · winner being finalized</>}
            </div>
            {leader ? (
              <>
                <div className="innov-leader-firm">{leader.display_ok ? leader.fund : 'A fund is leading'}</div>
                {leader.display_ok && leader.anchor_name && <div className="innov-leader-anchor">Anchor: {leader.anchor_name}</div>}
                <div className="innov-leader-amt">{money(leader.amount)}</div>
              </>
            ) : (
              <div className="innov-leader-empty">{phase === 'open' ? 'No bids yet. Be the first to lead the room.' : phase === 'opens' ? 'The board opens Monday.' : 'No sponsor this week.'}</div>
            )}
            {bidCount > 0 && (
              <div className="innov-leader-count">{bidCount === 1 ? '1 fund has bid this week' : `${bidCount} funds have bid this week`}</div>
            )}
            {phase === 'open' ? (
              <button type="button" className="innov-btn primary block" onClick={() => setModal('bid')}>
                {leader ? 'Outbid them →' : 'Place the first bid →'}
              </button>
            ) : (
              <button type="button" className="innov-btn primary block" disabled>
                {phase === 'opens' ? 'Bidding opens Monday' : 'Bidding closed'}
              </button>
            )}
          </div>

          <div className="innov-auction">
            <div>
              <h3>One seat in the room. Funds bid for it.</h3>
              <p className="innov-auction-sub">
                Every room has exactly one sponsor. It is the most direct line to fifteen of the
                best founders in the world, before anyone else reaches them. Because the seat is
                scarce and the room is closed, funds bid for it. Highest bid earns the seat.
              </p>
              <p className="innov-fine">The address in South Park is revealed only to confirmed guests.</p>
            </div>
            <div>
              <ul className="innov-rules">
                <li><span className="k">Opens</span><span>Monday, 12:00 PM PT</span></li>
                <li><span className="k">Closes</span><span>Tuesday, 12:00 PM PT</span></li>
                <li><span className="k">To bid</span><span>Place your bid below. Bids are public; your email stays private.</span></li>
                <li><span className="k">On win</span><span>We email the top bidder an invoice. Wire within 2 hours to lock the seat, or it passes to the next bid.</span></li>
              </ul>
              <button type="button" className="innov-btn primary block" onClick={() => setModal('bid')}>Place a bid →</button>
              <p className="innov-fine">Serious funds only. Invoiced via Stripe on win.</p>
            </div>
          </div>
        </section>

        <section className="innov-sec">
          <div className="innov-label">The Winners Ledger</div>
          <h2 className="innov-h2">Every Wednesday has a sponsor. Everyone sees who won.</h2>
          <div className="innov-ledger">
            <div className="innov-trophy live">
              <div className="innov-trophy-wk">This Wednesday</div>
              <div className="innov-trophy-main">
                <div className={`innov-trophy-firm${!leader && phase !== 'closed' ? ' placeholder' : ''}`}>
                  {leader
                    ? (leader.display_ok ? leader.fund : 'A fund is leading')
                    : (phase === 'closed' ? 'No sponsor this week' : 'Your Fund Name')}
                </div>
                <div className="innov-trophy-sub">
                  {phase === 'opens' && 'Bidding opens Monday 12:00 PM PT'}
                  {phase === 'open' && 'Bidding live · closes Tuesday 12:00 PM PT'}
                  {phase === 'closed' && 'Bidding closed · winner being finalized'}
                </div>
              </div>
              <div className="innov-trophy-amt">{leader ? money(leader.amount) : 'TBD'}</div>
              <div className="innov-trophy-stamp live">{phase === 'open' ? 'Open' : phase === 'opens' ? 'Soon' : 'Closed'}</div>
            </div>
            {ledger.map((r) => (
              <div className="innov-trophy" key={r.event_date}>
                <div className="innov-trophy-wk">{r.event_date}</div>
                <div className="innov-trophy-main">
                  <div className="innov-trophy-firm">{r.winner_fund || 'Sponsor'}</div>
                  {r.roster
                    ? <div className="innov-trophy-sub">Room: {r.roster}</div>
                    : (r.winner_anchor && <div className="innov-trophy-sub">Anchor: {r.winner_anchor}</div>)}
                </div>
                <div className="innov-trophy-amt">{r.winning_amount ? money(r.winning_amount) : ''}</div>
                <div className="innov-trophy-stamp">{r.winning_amount ? 'Sponsored' : 'Founding'}</div>
              </div>
            ))}
            {ledger.length === 0 && (
              <div className="innov-trophy dimmed">
                <div className="innov-trophy-wk">Coming</div>
                <div className="innov-trophy-main">
                  <div className="innov-trophy-firm">The first name goes here.</div>
                  <div className="innov-trophy-sub">Win a room and your fund leads the wall.</div>
                </div>
                <div className="innov-trophy-amt"></div>
                <div className="innov-trophy-stamp"></div>
              </div>
            )}
          </div>
          <p className="innov-note">Winning sponsors are published here at each Tuesday close.</p>
        </section>

      </main>
      <Footer theme="light" />

      {modal === 'bid' && (
        <Modal title="Bid for the sponsor seat" onClose={closeModal}>
          <BidForm eventDate={eventDate} onDone={refresh} />
        </Modal>
      )}
      {modal === 'apply' && (
        <Modal title="Apply for a seat" onClose={closeModal}>
          <ApplyForm />
        </Modal>
      )}
    </div>
  );
}

export default InnovatorsPage;
